<template>
    <div class="container-fluid px-3 px-md-4 py-3">
        <div class="qi-page-header d-flex justify-content-between align-items-start flex-wrap">
            <div>
                <h4 class="mb-0 text-primary">Sales</h4>
                <small class="text-muted">Invoices and estimates in one place.</small>
            </div>
            <div class="d-flex" style="gap:8px;">
                <a href="/quotes/create" class="btn btn-outline-primary"><i class="bi bi-plus-lg mr-1"></i> Create estimate</a>
                <a href="/invoices/create" class="btn btn-primary"><i class="bi bi-plus-lg mr-1"></i> Create invoice</a>
            </div>
        </div>

        <div v-if="stats" class="row mb-4" style="row-gap:18px;">
            <div class="col-6 col-md-4">
                <div class="text-secondary mb-1" style="font-size:12px;">Total Documents</div>
                <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ stats.total_count }}</span>
            </div>
            <div class="col-6 col-md-4">
                <div class="text-secondary mb-1" style="font-size:12px;">Invoices</div>
                <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ stats.invoice_count }}</span>
            </div>
            <div class="col-6 col-md-4">
                <div class="text-secondary mb-1" style="font-size:12px;">Estimates</div>
                <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ stats.quote_count }}</span>
            </div>
            <div class="col-6 col-md-4">
                <div class="text-secondary mb-1" style="font-size:12px;">Total Value</div>
                <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ formatMoney(stats.total_value) }}</span>
            </div>
            <div class="col-6 col-md-4">
                <div class="text-secondary mb-1" style="font-size:12px;">Total Outstanding</div>
                <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ formatMoney(stats.total_outstanding) }}</span>
            </div>
        </div>

        <div class="row qi-toolbar">
            <div class="col-6 col-md-3 mb-2 mb-md-0">
                <input type="text" class="form-control" placeholder="Search number" v-model="filters.search" @input="debouncedLoad">
            </div>
            <div class="col-6 col-md-3 mb-2 mb-md-0">
                <select class="form-control" v-model="filters.type" @change="onTypeChange">
                    <option value="">All types</option>
                    <option value="invoice">Invoices</option>
                    <option value="quote">Estimates</option>
                </select>
            </div>
            <div class="col-6 col-md-3 mb-2 mb-md-0">
                <select class="form-control" v-model="filters.status" @change="load">
                    <option value="">All statuses</option>
                    <option v-for="(meta, key) in statusOptions" :key="key" :value="key">{{ meta.label }}</option>
                </select>
            </div>
            <div class="col-6 col-md-3">
                <date-range-picker
                    :auto-apply="autoApply"
                    v-model="dateRange"
                    :ranges="ranges"
                    :opens="datepicker_opens"
                    :append-to-body="append_to_body"
                    class="w-100"
                >
                    <template #input="picker">
                        <span v-if="picker.startDate && picker.endDate">{{ formatDate(picker.startDate) }} - {{ formatDate(picker.endDate) }}</span>
                        <span v-else><i class="bi bi-calendar-event"></i> Filter by date</span>
                    </template>
                </date-range-picker>
            </div>
        </div>

        <div v-if="loading" class="text-center py-5"><span class="spinner-border"></span></div>

        <div v-else-if="!rows.length" class="empty-state">
            <i class="bi bi-receipt" style="font-size:2rem; opacity:.3;"></i>
            <p class="text-muted mt-2 mb-1">No invoices or estimates yet</p>
            <small class="text-muted d-block mb-3">Create your first <a href="/quotes/create">estimate</a> or <a href="/invoices/create">invoice</a> to start tracking sales.</small>
            <div class="d-flex justify-content-center" style="gap:8px;">
                <a href="/quotes/create" class="btn btn-outline-primary"><i class="bi bi-plus-lg mr-1"></i> Create estimate</a>
                <a href="/invoices/create" class="btn btn-primary"><i class="bi bi-plus-lg mr-1"></i> Create invoice</a>
            </div>
        </div>

        <template v-else>
            <div class="table-responsive d-none d-md-block">
                <table class="table table-sm table-striped qi-list-table">
                    <thead>
                        <tr>
                            <th>Number</th><th>Type</th><th>Customer</th><th>Date</th>
                            <th class="text-right">Total</th><th>Status</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="row.type + '-' + row.id" class="qi-row" @click="goTo(row)">
                            <td class="font-weight-bold">{{ row.number }}</td>
                            <td><span class="badge" :class="typeBadge(row.type)">{{ typeLabel(row.type) }}</span></td>
                            <td>{{ customerLabel(row.customer) }}</td>
                            <td>{{ formatDate(row.date) }}</td>
                            <td class="text-right">{{ formatMoney(row.total_amount) }}</td>
                            <td><span class="badge" :class="statusMeta(row).badge">{{ statusMeta(row).label }}</span></td>
                            <td class="text-right"><i class="bi bi-chevron-right text-muted"></i></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-md-none">
                <div class="qi-card-row" v-for="row in rows" :key="row.type + '-' + row.id" @click="goTo(row)">
                    <div class="d-flex justify-content-between">
                        <span class="font-weight-bold">{{ row.number }}</span>
                        <span class="badge" :class="typeBadge(row.type)">{{ typeLabel(row.type) }}</span>
                    </div>
                    <div class="text-muted small">{{ customerLabel(row.customer) }} &middot; {{ formatDate(row.date) }}</div>
                    <div class="d-flex justify-content-between mt-1">
                        <span class="font-weight-bold">{{ formatMoney(row.total_amount) }}</span>
                        <span class="badge" :class="statusMeta(row).badge">{{ statusMeta(row).label }}</span>
                    </div>
                </div>
            </div>

            <div v-if="pagination.last_page > 1" class="d-flex justify-content-center mt-3">
                <b-pagination v-model="pagination.current_page" :total-rows="pagination.total" :per-page="pagination.per_page" size="sm" @change="onPage"></b-pagination>
            </div>
        </template>
    </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import { formatMoney, invoiceStatusMeta, quoteStatusMeta, customerLabel, INVOICE_STATUSES, QUOTE_STATUSES } from './docHelpers';

export default {
    name: 'SalesList',
    components: { DateRangePicker },
    data() {
        return {
            loading: true,
            rows: [],
            filters: { search: '', type: '', status: '' },
            dateRange: { startDate: null, endDate: null },
            autoApply: true,
            append_to_body: true,
            datepicker_opens: 'right',
            ranges: false,
            pagination: { current_page: 1, last_page: 1, total: 0, per_page: 20 },
            debounceTimer: null,
            stats: null,
        };
    },
    computed: {
        currency() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings && u.company.companysettings.currency) || 'KSh';
        },
        // Status vocabularies differ by type (invoice: unpaid/partial/paid/overdue/cancelled
        // vs quote: draft/sent/accepted/converted/expired/cancelled) - show the one that
        // matches the selected type, or every status when no type is picked.
        statusOptions() {
            if (this.filters.type === 'invoice') return INVOICE_STATUSES;
            if (this.filters.type === 'quote') return QUOTE_STATUSES;
            return { ...INVOICE_STATUSES, ...QUOTE_STATUSES };
        },
    },
    watch: {
        dateRange: {
            deep: true,
            handler(after) {
                if (after.startDate && after.endDate) {
                    this.pagination.current_page = 1;
                    this.load();
                }
            },
        },
    },
    created() { this.load(); this.loadStats(); },
    methods: {
        customerLabel,
        formatMoney(v) { return formatMoney(v, this.currency); },
        formatDate(d) { return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
        typeLabel(type) { return type === 'invoice' ? 'Invoice' : 'Estimate'; },
        typeBadge(type) { return type === 'invoice' ? 'badge-dark' : 'badge-secondary'; },
        statusMeta(row) { return row.type === 'invoice' ? invoiceStatusMeta(row.status) : quoteStatusMeta(row.status); },
        onTypeChange() {
            // The status filter's meaning depends on type - a status picked
            // under one type may not exist in the other's vocabulary.
            this.filters.status = '';
            this.load();
        },
        async loadStats() {
            try {
                const { data } = await axios.get('/api/sales-documents/stats');
                this.stats = data;
            } catch (e) {
                // Stats are a nice-to-have; the list itself still works without them.
            }
        },
        debouncedLoad() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(this.load, 300);
        },
        async load() {
            this.loading = true;
            try {
                const { data } = await axios.get('/api/sales-documents', {
                    params: {
                        search: this.filters.search || undefined,
                        type: this.filters.type || undefined,
                        status: this.filters.status || undefined,
                        from: this.dateRange.startDate ? this.dateRange.startDate.toISOString().split('T')[0] : undefined,
                        to: this.dateRange.endDate ? this.dateRange.endDate.toISOString().split('T')[0] : undefined,
                        page: this.pagination.current_page,
                    },
                });
                this.rows = data.data || [];
                this.pagination = {
                    current_page: data.current_page, last_page: data.last_page,
                    total: data.total, per_page: data.per_page,
                };
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: 'Could not load sales documents.' });
            } finally {
                this.loading = false;
            }
        },
        onPage(page) { this.pagination.current_page = page; this.load(); },
        goTo(row) { window.location.href = row.type === 'invoice' ? `/invoices/${row.id}` : `/quotes/${row.id}`; },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
@media (max-width: 768px) { .container-fluid { padding-bottom: 90px; } }
.qi-page-header { margin-bottom: 14px; }
.qi-toolbar { margin-bottom: 12px; }
.qi-list-table th { font-size: .75rem; text-transform: uppercase; color: #6c757d; border-top: none; padding: 10px 12px; }
.qi-list-table td { padding: 14px 12px; vertical-align: middle; }
.qi-row { cursor: pointer; }
.qi-row:hover { filter: brightness(0.97); }
.qi-card-row {
    border: 1px solid #eee; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; cursor: pointer;
}
.empty-state { text-align: center; padding: 48px 16px; }
</style>
